import React, { useState, useEffect } from 'react';
import AddMetodos from './AddMetodos';
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper, TableHead, Button, TablePagination, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';

function GestionMetodos() {
    const { tienePermiso } = useAccesos();
    const server = process.env.REACT_APP_API_ROUTE;
    const key = process.env.REACT_APP_API_KEY;
    const [metodos, setMetodos] = useState([]);
    const [editingMetodo, setEditingMetodo] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [metodoToDelete, setMetodoToDelete] = useState(null);
    const { user } = useUser();

    const fetchMetodos = async () => {
        try {
            const response = await fetch(`${server}/MgAnalisisGeneralMetodo`);
            const data = await response.json();
            setMetodos(data.$values || data);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Error al cargar los métodos: ' + error.message,
            });
        }
    };

    useEffect(() => {
        fetchMetodos();
    }, []);

    const handleNewMetodo = () => {
        fetchMetodos();
        setEditingMetodo(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const handleOpenDialog = (metodo) => {
        setMetodoToDelete(metodo);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setMetodoToDelete(null);
    };

    const handleDeleteMetodo = async () => {
        if (metodoToDelete) {
            try {
                const username = encodeURIComponent(user.codigoUsuario || '');
                const response = await fetch(`${server}/MgAnalisisGeneralMetodo/${metodoToDelete.id}/${username}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': key
                    }
                });

                if (response.ok) {
                    handleCloseDialog();
                    fetchMetodos();
                    Swal.fire({
                        icon: 'success',
                        title: 'Éxito',
                        text: 'Método eliminado con éxito',
                    });
                } else {
                    handleCloseDialog();
                    const errorData = await response.json();
                    const errorMessage = errorData.message || (errorData.errors ? Object.values(errorData.errors).flat().join(', ') : 'Ocurrió un error inesperado');
                    Swal.fire({
                        icon: 'error',
                        title: `Error ${response.status}`,
                        text: errorMessage,
                    });
                }
            } catch (error) {
                handleCloseDialog();
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Error al eliminar el método: ' + error.message,
                });
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return '-';
        }
    };

    const filteredMetodos = metodos.filter(metodo => 
        metodo.descripcion?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <div className="x_title">
                {/* Title can go here if needed */}
            </div>
            <div className="x_content" style={{ padding: '0 15px' }}>
                <br />
                <AddMetodos onNewMetodo={handleNewMetodo} editingMetodo={editingMetodo} />
                <div className="ln_solid" />

                <h2>Métodos Existentes</h2>

                <div className="row">
                    <div className="col-md-7 col-sm-4 offset-md-2">
                        <TextField
                            variant="outlined"
                            label="Buscar método"
                            fullWidth
                            value={searchQuery}
                            onChange={handleSearchChange}
                            style={{ marginBottom: '20px' }}
                        />
                        <TableContainer component={Paper}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ minWidth: '60px' }}>ID</TableCell>
                                        <TableCell style={{ minWidth: '200px' }}>Descripción</TableCell>
                                        <TableCell style={{ minWidth: '120px' }}>Ingresado Por</TableCell>
                                        <TableCell style={{ minWidth: '140px' }}>Fecha Ingreso</TableCell>
                                        <TableCell style={{ minWidth: '120px' }}>Modificado Por</TableCell>
                                        <TableCell style={{ minWidth: '140px' }}>Fecha Modificación</TableCell>
                                        <TableCell align="right" style={{ minWidth: '140px' }}>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredMetodos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((metodo) => (
                                        <TableRow key={metodo.id}>
                                            <TableCell component="th" scope="row">{metodo.id}</TableCell>
                                            <TableCell>{metodo.descripcion}</TableCell>
                                            <TableCell>{metodo.ingresadoPor}</TableCell>
                                            <TableCell>{formatDate(metodo.fechaIngreso)}</TableCell>
                                            <TableCell>{metodo.modificadoPor || '-'}</TableCell>
                                            <TableCell>{formatDate(metodo.fechaModificacion)}</TableCell>
                                            <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                                                <Button 
                                                    onClick={() => setEditingMetodo(metodo)} 
                                                    color="primary"
                                                    size="small"
                                                    style={{ marginRight: '4px', fontSize: '0.75rem' }}
                                                    disabled={!tienePermiso('Editar Método')}
                                                >
                                                    Editar
                                                </Button>
                                                <Button 
                                                    onClick={() => handleOpenDialog(metodo)} 
                                                    color="secondary"
                                                    size="small"
                                                    style={{ fontSize: '0.75rem' }}
                                                    disabled={!tienePermiso('Eliminar Método')}
                                                >
                                                    Eliminar
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={filteredMetodos.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage="Filas por página:"
                                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                            />
                        </TableContainer>
                        
                        <Dialog
                            open={openDialog}
                            onClose={handleCloseDialog}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                        >
                            <DialogTitle id="alert-dialog-title">{"Confirmar eliminación"}</DialogTitle>
                            <DialogContent>
                                <DialogContentText id="alert-dialog-description">
                                    ¿Estás seguro de que deseas eliminar el método "{metodoToDelete?.descripcion}"?
                                    Esta acción no se puede deshacer.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDialog} color="primary">
                                    Cancelar
                                </Button>
                                <Button onClick={handleDeleteMetodo} color="primary" autoFocus>
                                    Eliminar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </div>
                </div>
            </div>
        </>
    );
}

export default GestionMetodos;
